import Pagination from "@/types/Pagination.interface"
import PaginationParams from "@/types/PaginationParams.interface"
import ShortLink from "@/types/ShortLink.interface"

interface State {
    routesViewRoutes: ShortLink[]
    routesViewRoutesCurrentPage: number
    routesViewRoutesLastPage: number
    routesViewRoutesNextPage: number
    routesViewRoutesParams: PaginationParams
    routesViewSelectedRoute: ShortLink
    routesViewPaginating: boolean
}

const defaults = () => {
    return {
        routesViewRoutes: [],
        routesViewRoutesCurrentPage: null,
        routesViewRoutesLastPage: null,
        routesViewRoutesNextPage: null,
        routesViewSelectedRoute: null,
        routesViewPaginating: false,
        routesViewRoutesParams: {
            page: 1
        }
    }
}

export default {
    state: defaults(),
    mutations: {
        routesViewRoutesParamsSet (state: State, params: PaginationParams): void {
            state.routesViewRoutesParams = params
        },
        routesViewRoutesPush (state: State, routes: ShortLink[]): void {
            //  Update the state
            state.routesViewRoutes = window.removeDuplicatesById(state.routesViewRoutes, routes)

            //  Get the currently selected routes
            const selected = state.routesViewSelectedRoute

            //  If no selected route, do nothing
            if (!selected) return

            //  Else, check if they were updated
            const updated = routes.find((x: ShortLink) => x.id === selected.id)

            //  If it wasn't, we're done
            if (!updated) return

            //  Else, replace the route
            state.routesViewSelectedRoute = updated
        },
        routesViewRoutesRemove (state: State, ids: number[]): void {
            //  Get our current list of routes
            const routes = state.routesViewRoutes

            //  If no routes, do nothing
            if (!routes) return

            //  Else, filter our our removed ids
            const filtered = routes.filter(x => !ids.includes(x.id))

            //  Set our routes to the filtered list
            state.routesViewRoutes = filtered

            //  Get the currently selected route
            const selected = state.routesViewSelectedRoute

            //  If nothing is selected, we're done
            if (!selected) return

            //  Else, check if they were deleted
            const deleted = ids.find((x: number) => x == selected.id)

            //  If the selected route was not found, do nothing
            if (!deleted) return

            //  Else, set our selected to null
            state.routesViewSelectedRoute = null
        },
        routesViewRoutesSync (state: State, updates: ShortLink[]): void {
            //  Grab our current routes
            const routes = state.routesViewRoutes

            //  Update the routes
            const updatedRoutes = window.removeDuplicatesById(routes, updates)

            //  Sort our routes
            updatedRoutes.sort((a: ShortLink, b: ShortLink) => {
                return b.id - a.id
            })

            //  Update our state
            state.routesViewRoutes = updatedRoutes

            //  Get the currently selected route
            const selected = state.routesViewSelectedRoute

            //  If no selected route, do nothing
            if (!selected) return

            //  Else, check if we updated it
            const wasUpdated = updates.find((x: ShortLink) => x.id === selected.id)

            //  If it wasn't updated, do nothing
            if (!wasUpdated) return

            //  Else, replace the route
            state.routesViewSelectedRoute = wasUpdated
        },
        routesViewPaginationPush (state: State, page: Pagination): void {
            //  If this is page one, reset the the routes list
            if (page.current_page === 1) state.routesViewRoutes = []

            state.routesViewRoutesCurrentPage = page.current_page
            state.routesViewRoutesLastPage = page.last_page
            state.routesViewRoutesNextPage = page.current_page + 1
        },
        routesViewSelectedRouteSet (state: State, route: ShortLink): void {
            state.routesViewSelectedRoute = route
        },
        routesViewPaginatingSet (state: State, bool: boolean): void {
            state.routesViewPaginating = bool
        },
        routesViewReset (state: State): void {
            Object.assign(state, defaults())
        }
    },
    actions: {
        routesViewRoutesPaginate ({ commit, getters }): Promise<Pagination> {
            //  Select our pagination params
            const p = getters.routesViewRoutesParams

            //  Start the paginating tracker
            commit('routesViewPaginatingSet', true)

            //  Assemble the API endpoint
            const url = `${getters.api}/routes?${window.toUrlParams(p)}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set our pagination
                commit('routesViewPaginationPush', response.data)

                //  Update the routes view routes list
                commit('routesViewRoutesPush', response.data.data)

                //  Stop the 'paginating' tracker
                commit('routesViewPaginatingSet', false)

                //  Return the pagination data
                return response.data
            }).catch(() => null)
        },
        routesViewSelectedRouteGet ({ commit, getters }, routeId: number): Promise<ShortLink> {
            //  Grab our global routes list
            const routes = getters.routes

            //  Check if our id exists in there
            const existingRoute = routes.find((x: ShortLink) => x.id == routeId)

            //  If it does exist, then commit and return
            if (existingRoute) return commit('routesViewSelectedRouteSet', existingRoute)

            //  Else, assemble our API endpoint
            const url = `${getters.api}/routes/${routeId}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set our selected route
                commit('routesViewSelectedRouteSet', response.data)

                //  Push this route to the global set
                commit('routesPush', [response.data])

                //  Return the found route
                return response.data
            }).catch(() => null)
        }
    },
    getters: {
        routesViewRoutes (state: State): ShortLink[] {
            return state.routesViewRoutes
        },
        routesViewRoutesCurrentPage (state: State): number {
            return state.routesViewRoutesCurrentPage
        },
        routesViewRoutesLastPage (state: State): number {
            return state.routesViewRoutesLastPage
        },
        routesViewRoutesNextPage (state: State): number {
            return state.routesViewRoutesNextPage
        },
        routesViewRoutesParams (state: State): PaginationParams {
            return state.routesViewRoutesParams
        },
        routesViewPaginating (state: State): boolean {
            return state.routesViewPaginating
        },
        routesViewSelectedRoute (state: State): ShortLink {
            return state.routesViewSelectedRoute
        }
    }
}
